import { IInteractionMessagesGetQueryParameters, IInteractionsGetQueryParameters } from "@edgetier/watchtower-types";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { pascalize } from "humps";

type IExcludedParameterNames = Readonly<Array<keyof IInteractionsGetQueryParameters>>;

// Remove the excluded parameters from IInteractionMessagesGetQueryParameters.
type IParametersWithoutExclusions<T extends IExcludedParameterNames> = {
    [Key in keyof IInteractionMessagesGetQueryParameters as Key extends `interaction${Capitalize<T[number]>}`
        ? never
        : Key extends T[number]
        ? never
        : Key]: IInteractionMessagesGetQueryParameters[Key];
};

// The excluded paramters without the interaction prefix.
type IExcludedParameters<T extends IExcludedParameterNames> = {
    [Key in T[number]]: Key extends keyof IInteractionMessagesGetQueryParameters
        ? IInteractionMessagesGetQueryParameters[Key]
        : IInteractionMessagesGetQueryParameters[`interaction${Capitalize<Key>}`];
};

type IPrefixedParametersWithoutExclusions<T extends IExcludedParameterNames> = IParametersWithoutExclusions<T> &
    IExcludedParameters<T>;

/**
 * Get interaction query parameters and rename them using a prefix
 * @param defaultValues Any defaults for misisng parameters
 * @param additionalParameters Any parameters added to the overall returned parameters
 * @param prefixValue String applied as prefix for each key
 * @param excludedParameters Filter keys excluded from renaming
 */
export const useInteractionsMessageQueryParameters = <Excluded extends IExcludedParameterNames>({
    defaultValues = {},
    additionalParameters,
    prefixValue,
    excludedParameters,
}: {
    defaultValues?: Record<string, any>;
    additionalParameters: Record<string, any>;
    prefixValue: string;
    excludedParameters: Excluded;
}) => {
    const queryParameters = useInteractionQueryParameters({ defaults: defaultValues });
    const allParameters: IInteractionsGetQueryParameters = { ...queryParameters, ...additionalParameters };
    const parameterKeys = Object.keys(allParameters) as Array<keyof IInteractionsGetQueryParameters>;

    const renamedParameters = parameterKeys.reduce<IPrefixedParametersWithoutExclusions<Excluded>>(
        (parameters, key) => {
            let updatedKeyFilter = {};
            if (excludedParameters.includes(key)) {
                updatedKeyFilter = { [key]: allParameters[key] };
            } else {
                const newKey = pascalize(key);
                updatedKeyFilter = {
                    [`${prefixValue}${newKey}`]: allParameters[key],
                };
            }
            return {
                ...parameters,
                ...updatedKeyFilter,
            };
        },
        {} as IPrefixedParametersWithoutExclusions<Excluded>
    );

    return renamedParameters;
};
